import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { DashShell, Badge } from "@/components/dash/DashShell";
import { ADMIN_NAV } from "./nav";
import { Input } from "@/components/ui/input";
import { api, inr } from "@/lib/api";

export default function AdminLeads() {
    const nav = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", lead_status: "", payment_status: "", source: "" });
    const [statuses, setStatuses] = useState([]);

    useEffect(() => { api.get("/meta").then(({ data }) => setStatuses(data.lead_statuses)); }, []);

    const load = useCallback(() => {
        setLoading(true);
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        api.get("/admin/leads", { params }).then(({ data }) => setLeads(data)).finally(() => setLoading(false));
    }, [filters]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

    return (
        <DashShell nav={ADMIN_NAV} title="Lead Management">
            <div className="bg-white rounded-2xl border border-black/5 p-4 shadow-sm mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-teal-900/40" /><Input className="pl-9" placeholder="Search name, mobile, business, ID" value={filters.q} onChange={setF("q")} data-testid="leads-search" /></div>
                <Sel value={filters.lead_status} onChange={setF("lead_status")} testid="filter-status"><option value="">All Statuses</option>{statuses.map((s) => <option key={s} value={s}>{s}</option>)}</Sel>
                <Sel value={filters.payment_status} onChange={setF("payment_status")} testid="filter-payment"><option value="">All Payments</option>{["Paid", "Pending", "Failed", "Not Applicable"].map((s) => <option key={s} value={s}>{s}</option>)}</Sel>
                <Sel value={filters.source} onChange={setF("source")} testid="filter-source"><option value="">All Sources</option>{["direct", "referral", "google", "meta", "instagram", "facebook", "contact"].map((s) => <option key={s} value={s}>{s}</option>)}</Sel>
            </div>

            <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-x-auto">
                {loading ? <div className="grid place-items-center py-24 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
                    <table className="w-full min-w-[900px] text-sm" data-testid="leads-table">
                        <thead className="bg-teal-900/5 text-teal-900/60 text-xs uppercase">
                            <tr>{["Lead ID", "Date", "Customer", "Business", "Mobile", "City", "Service", "Amount", "Payment", "Status", "BDE", "Source"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {leads.map((l) => (
                                <tr key={l.lead_id} onClick={() => nav(`/admin/leads/${l.lead_id}`)} className="hover:bg-black/[0.02] cursor-pointer" data-testid={`lead-row-${l.lead_id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-teal-700">{l.lead_id}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-teal-900/60">{(l.created_at || "").slice(0, 10)}</td>
                                    <td className="px-4 py-3 font-medium text-teal-900">{l.customer_name}</td>
                                    <td className="px-4 py-3 text-teal-900/70">{l.business_name || "—"}</td>
                                    <td className="px-4 py-3 text-teal-900/70">{l.mobile}</td>
                                    <td className="px-4 py-3 text-teal-900/70">{l.city || "—"}</td>
                                    <td className="px-4 py-3 text-teal-900/70 max-w-[160px] truncate">{l.package || l.service || "—"}</td>
                                    <td className="px-4 py-3 font-mono">{l.amount ? inr(l.amount) : "—"}</td>
                                    <td className="px-4 py-3"><Badge status={l.payment_status} /></td>
                                    <td className="px-4 py-3"><Badge status={l.lead_status} /></td>
                                    <td className="px-4 py-3 text-teal-900/70">{l.assigned_bde_name || <span className="text-teal-900/30">Unassigned</span>}</td>
                                    <td className="px-4 py-3 text-teal-900/50 capitalize">{l.source}</td>
                                </tr>
                            ))}
                            {!leads.length && <tr><td colSpan={12} className="text-center py-16 text-teal-900/40">No leads found</td></tr>}
                        </tbody>
                    </table>
                )}
            </div>
        </DashShell>
    );
}

function Sel({ children, testid, ...props }) {
    return <select className="h-9 rounded-md border border-input bg-white px-3 text-sm" data-testid={testid} {...props}>{children}</select>;
}
